import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { FontFamily } from '@euler/components/typography/fonts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { PieChart } from '@euler/lib/charts/PieChart';
import { formatBiValue } from '@euler/model/bi/format';
import { BiFormat } from '@euler/model/bi/viz';
import { memo } from 'react';
import { View } from 'react-native';
import { BiChartModel } from '../functions/biChartModel';
import { BiVizLook } from '../functions/biVizLook';

type Pie = Extract<BiChartModel, { kind: 'pie' }>;

/** A slice smaller than this share carries no text: it would not fit. */
const kMinTextShare = 0.04;

/** A slice's share as text, `12.5%`. */
export function biShareText(value: number, total: number): string {
  return formatBiValue(total ? value / total : 0, BiFormat.Percent);
}

/** A pie or donut: hole size, percent and value labels as styled. */
export const BiPieChart = memo(
  ({ model, look, size }: { model: Pie; look: BiVizLook; size: number }) => {
    const { rpx } = useResponsiveContext();
    const radius = size / 2;
    const inner = Math.round(look.pie.innerRadius * radius);
    const sum = model.slices.reduce((total, slice) => total + slice.value, 0);
    const text = (value: number, label: string) => {
      if (!sum || value / sum < kMinTextShare) return '';
      if (look.pie.showPercent) return biShareText(value, sum);
      return look.dataLabels ? label : '';
    };
    const anyText = look.pie.showPercent || look.dataLabels;
    return (
      <View style={{ alignItems: 'center' }}>
        <PieChart
          data={model.slices.map(slice => ({
            value: slice.value,
            color: slice.color,
            text: text(slice.value, slice.label),
          }))}
          radius={radius}
          donut={inner > 0}
          innerRadius={inner > 0 ? inner : undefined}
          showText={anyText}
          textColor="#FFFFFF"
          textSize={rpx(10)}
          font={FontFamily.NotoSans.Regular}
          labelsPosition="mid"
          centerLabelComponent={
            inner > rpx(28)
              ? () => (
                  <Typography.Text
                    bold
                    size={rpx(13)}
                    color={aiColors.foreground}
                  >
                    {model.total}
                  </Typography.Text>
                )
              : undefined
          }
          isAnimated
        />
      </View>
    );
  },
);
